import { useState, useEffect, memo, useCallback } from "react";
import { Menu, X } from "lucide-react";
import { Button, ContactButton } from "@/components/ui/button";
import { ServicesMenu } from "./navbar/ServicesMenu";
import { SpecializationsMenu } from "./navbar/SpecializationsMenu";
import { MobileMenu } from "./navbar/MobileMenu";
import { Link, useLocation } from "react-router-dom";
import { useMenuToggle } from "@/hooks/useMenuToggle";
import { useScrollPosition } from "@/hooks/useScrollPosition";

/**
 * Main navigation component for the application
 * Includes desktop and mobile navigation with dropdown menus
 * @returns {JSX.Element} The Navbar component
 */
export const Navbar = memo(() => {
  const isScrolled = useScrollPosition(10);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isSpecializationsOpen, setIsSpecializationsOpen] = useState(false);
  const location = useLocation();
  
  // Use custom hook for mobile menu toggle
  const { isOpen: isMobileMenuOpen, toggle: toggleMobileMenu, close: closeMobileMenu } = useMenuToggle(false);

  // Handle menu toggles with memoization
  const handleServicesToggle = useCallback(() => {
    setIsServicesOpen(prev => {
      const newValue = !prev;
      if (newValue) setIsSpecializationsOpen(false);
      return newValue;
    });
  }, []);

  const handleSpecializationsToggle = useCallback(() => {
    setIsSpecializationsOpen(prev => {
      const newValue = !prev;
      if (newValue) setIsServicesOpen(false);
      return newValue;
    });
  }, []);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  const shouldShowGlassEffect = isScrolled || isServicesOpen || isSpecializationsOpen;
  const shouldShowBorder = isScrolled || isServicesOpen || isSpecializationsOpen;

  // Error boundary wrapper
  try {
    return (
      <>
        <div className="fixed top-0 left-0 right-0 z-50 px-4 py-3 sm:py-4">
          <nav
            className={`max-w-7xl mx-auto transition-all duration-300 rounded-full ${
              shouldShowGlassEffect 
                ? "glass-effect" 
                : "bg-transparent"
            } ${
              shouldShowBorder 
                ? "shadow-lg border border-white/10" 
                : ""
            }`}
          >
            <div className="container mx-auto px-4 sm:px-6">
              <div className="flex items-center justify-between h-16">
                <div className="flex items-center">
                  <a href="/" className="block transition-transform duration-200 hover:scale-105">
                    <img
                      src="/lovable-uploads/6e88f8b3-0ddf-4ad8-9625-adcf5c8d9671.png"
                      alt="EdgeFi Logo"
                      className="h-10 sm:h-12 w-auto drop-shadow-md"
                    />
                  </a>
                </div>

                {/* Desktop Menu */}
                <div className="hidden lg:flex items-center space-x-8">
                  {/* Services Dropdown */}
                  <div className="relative group">
                    <button
                      onClick={handleServicesToggle}
                      className={`story-link flex items-center space-x-1 text-base font-normal transition-colors ${
                        isServicesOpen ? "text-edgefi-purple" : "text-gray-700 hover:text-edgefi-purple"
                      }`}
                    >
                      <span>Services</span>
                    </button>
                    <ServicesMenu isOpen={isServicesOpen} />
                  </div>
                  
                  {/* Specializations Dropdown */}
                  <div className="relative group">
                    <button
                      onClick={handleSpecializationsToggle}
                      className={`story-link flex items-center space-x-1 text-base font-normal transition-colors ${
                        isSpecializationsOpen ? "text-edgefi-purple" : "text-gray-700 hover:text-edgefi-purple"
                      }`}
                    >
                      <span>Specializations</span>
                    </button>
                    <SpecializationsMenu isOpen={isSpecializationsOpen} />
                  </div>
                  
                  <Link
                    to="/about"
                    className={`story-link text-base font-normal transition-colors ${
                      location.pathname === "/about" ? "text-edgefi-purple" : "text-gray-700 hover:text-edgefi-purple"
                    }`}
                  >
                    About Us
                  </Link>
                  <ContactButton 
                    variant="cta" 
                    size="lg"
                    className="text-white rounded-full"
                    modalTitle="Contact Us"
                    modalDescription="Have questions? Get in touch with our team."
                  >
                    Contact Us
                  </ContactButton>
                </div>

                {/* Mobile Menu Button */}
                <div className="lg:hidden">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={toggleMobileMenu}
                    className="text-gray-700 p-2 focus:ring focus:ring-purple-100 rounded-full"
                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </div>
              </div>
            </div>
          </nav>
        </div>
        
        {/* Mobile Menu using Sheet component */}
        <MobileMenu isOpen={isMobileMenuOpen} onClose={closeMobileMenu} />
      </>
    );
  } catch (error) {
    console.error("Error rendering Navbar:", error);
    // Fallback minimal navbar in case of error
    return (
      <div className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 py-4">
          <a href="/" className="block">
            <img
              src="/lovable-uploads/6e88f8b3-0ddf-4ad8-9625-adcf5c8d9671.png"
              alt="EdgeFi Logo"
              className="h-10 w-auto"
            />
          </a>
        </div>
      </div>
    );
  }
});

Navbar.displayName = "Navbar";
